import React from 'react';
import { useLocalSearchParams } from 'expo-router';

import { useIsFocused } from '@garnish/core';

import {
  MenuContent,
  useDeliveryMenuContentData,
  useMenuContent,
  useProductDetailsContentData,
} from '../../components';
import { useMenuReorderRailOrders } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const DeliveryMenuScreen = () => {
  //
  // ─── Navigation ──────────────────────────────────────────────────────

  const { addressId } = useLocalSearchParams<{ addressId: string }>();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { restaurantData, deliveryDetailsData, isFetching, hasError, hasNoProducts } =
    useDeliveryMenuContentData({ addressId });

  const { productDetailsContentData } = useProductDetailsContentData();
  const { mappedContent } = useMenuContent();

  const { categoriesBadges, caloriesOverrides, denseCategoryIds, caloriesOnlyCategoryIds } =
    mappedContent;

  const numericRestaurantId = restaurantData ? Number.parseInt(restaurantData.id, 10) : 0;

  const reorderRailOrders = useMenuReorderRailOrders({
    restaurantId: Number.isNaN(numericRestaurantId) ? 0 : numericRestaurantId,
    enabled: Boolean(restaurantData),
  });

  // ─────────────────────────────────────────────────────────────────────

  const isScreenFocused = useIsFocused();
  const shouldRenderErrorView =
    hasError || !restaurantData || !deliveryDetailsData || hasNoProducts;

  // When the menu screen is rendered without being focused, there won't be any data available.
  const isWaitingForFocus = !isScreenFocused && shouldRenderErrorView;

  if (isFetching || isWaitingForFocus || reorderRailOrders.isFetchingMostRecentOrders) {
    return <MenuContent.Loading />;
  }

  if (shouldRenderErrorView) {
    return <MenuContent.Error />;
  }

  // ─────────────────────────────────────────────────────────────────────

  const locationName = deliveryDetailsData.addressName ?? deliveryDetailsData?.shortAddress;

  return (
    <MenuContent.Ready
      // NOTE: We use the address ID as the key to force a re-render when the
      //       delivery details data changes to prevent having a stale data and components.
      key={deliveryDetailsData.addressId}
      // @ts-expect-error - restaurantData missing sanitaryGradeDisplayText/sanitaryGradeUrl
      restaurantData={restaurantData}
      productDetailsContentData={productDetailsContentData}
      categoriesBadges={categoriesBadges}
      caloriesOverrides={caloriesOverrides}
      denseCategoryIds={denseCategoryIds}
      caloriesOnlyCategoryIds={caloriesOnlyCategoryIds}
      deliveryDetailsData={deliveryDetailsData}
      orderChannel="delivery"
      locationName={locationName}
      reorderRailOrders={reorderRailOrders}
    />
  );
};
